import { API_CONFIG } from '../config/api.config';
import { apiClient } from './api.client';

export interface CompanySummary {
  company_id: number; name: string; parent_company: number | null;
  legacy_status: string; legacy_valid_until: string | null;
  locations: number; users: number; mode: 'legacy' | 'shadow' | 'enforce'; policy_status: string;
  parent_name?: string | null; customer_type?: CustomerType; children?: number;
  shared_locations?: number;
}
export type CustomerType = 'custom' | 'home' | 'business' | 'provider';
export interface WorkspaceSettings { customer_type: CustomerType; location_overrides: Record<string, Record<string, boolean>>; }
export interface CompanyPolicy {
  version: number; mode: 'shadow' | 'enforce'; status: 'active' | 'read_only' | 'suspended';
  active_from: string | null; active_until: string | null; grace_days: number;
  features: Record<string, boolean>; face_providers: ('local' | 'rekognition')[];
  workspace?: WorkspaceSettings;
}
export interface EffectivePolicy {
  status: string; features: Record<string, boolean>; face_providers: string[]; reasons: string[]; enforced: boolean;
}
export interface PlatformPermission {
  screen_id: number; screen_code: string; screen_name: string;
  can_view: boolean; can_create: boolean; can_edit: boolean; can_delete: boolean; can_export: boolean;
}
export interface CompanyDetail extends CompanySummary {
  policy: CompanyPolicy; effective: EffectivePolicy; preview: EffectivePolicy;
  feature_catalog: Record<string, string>;
  configured: Record<string, Record<string, unknown> | null>;
  users_detail: { profile_id: number; firstname: string; lastname: string; email: string; status: string; role_id: number | null; platform_admin: number }[];
  workspace_supported?: boolean;
  inherited?: EffectivePolicy;
  locations_detail?: { location_id: number; name: string; owner_company_id: number; effective: EffectivePolicy; inherited?: EffectivePolicy }[];
  children_detail?: CompanySummary[]; parent_detail?: CompanySummary | null;
}
export interface AuditEntry {
  id: number; actor_id: number; company_id: number | null; action: string; reason: string;
  before_json: unknown; after_json: unknown; created_at: string;
}

// Elevated credentials are held only in memory, separately from the company login.
let elevatedToken: string | null = null;

async function request<T>(path: string, method = 'GET', data?: unknown): Promise<T> {
  if (!elevatedToken) throw new Error('Unlock the platform console first.');
  const response = await fetch(`${API_CONFIG.BASE_URL}/platform${path}`, {
    method, headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${elevatedToken}` },
    ...(data !== undefined ? { body: JSON.stringify(data) } : {}),
  });
  const body = await response.json().catch(() => ({}));
  if (response.status === 401 || response.status === 403) {
    elevatedToken = null;
    window.dispatchEvent(new Event('platform-session-ended'));
  }
  if (!response.ok) throw new Error(typeof body.detail === 'string' ? body.detail : 'The request could not be completed. Check the form and retry.');
  return body;
}

export const platformAdminService = {
  me: () => apiClient.get<{ platform_admin: boolean }>('/platform/me'),
  async unlock(password: string, code: string) {
    const response = await apiClient.post<{ access_token: string; expires_at: string }>('/platform/session', { password, code });
    elevatedToken = response.access_token;
    return response;
  },
  async lock() {
    try { if (elevatedToken) await request('/session', 'DELETE'); }
    finally { elevatedToken = null; }
  },
  clear() { elevatedToken = null; },
  companies: (search = '', page = 1, type?: CustomerType) => request<{ items: CompanySummary[]; total: number; summary?: { accounts: number; providers: number; locations: number; restricted: number } }>(`/companies?search=${encodeURIComponent(search)}&page=${page}${type ? `&customer_type=${type}` : ''}`),
  company: (id: number) => request<CompanyDetail>(`/companies/${id}`),
  savePolicy: (id: number, policy: CompanyPolicy, reason: string) => {
    const utc = (value: string | null) => value ? new Date(/Z$|[+-]\d\d:\d\d$/.test(value) ? value : `${value}Z`).toISOString() : null;
    return request<CompanyDetail>(`/companies/${id}/policy`, 'PUT', {
      ...policy, active_from: utc(policy.active_from), active_until: utc(policy.active_until), reason,
    });
  },
  permissions: (id: number, role: number) => request<{ permissions: PlatformPermission[] }>(`/companies/${id}/permissions/${role}`),
  savePermission: (id: number, role: number, permission: PlatformPermission, reason: string) => request(`/companies/${id}/permissions/${role}`, 'PUT', {
    screen_code: permission.screen_code, permissions: {
      can_view: permission.can_view, can_create: permission.can_create, can_edit: permission.can_edit,
      can_delete: permission.can_delete, can_export: permission.can_export,
    }, reason,
  }),
  status: (id: number, profile: number, active: boolean, reason: string) => request(`/companies/${id}/users/${profile}/status`, 'PUT', { active, reason }),
  revoke: (id: number, profile: number, reason: string) => request(`/companies/${id}/users/${profile}/revoke-sessions`, 'POST', { reason }),
  audit: (id: number, before?: number) => request<{ items: AuditEntry[]; next_before: number | null }>(`/audit?company=${id}${before ? `&before=${before}` : ''}`),
};
